// React imports
import { useState, useMemo, useEffect } from 'react';

// Other imports
import _ from 'lodash';
import dayjs from 'dayjs';

// Material UI imports
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepButton from '@mui/material/StepButton';
import Alert from '@mui/material/Alert';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import axios from 'axios';
import { useTheme, alpha } from '@mui/material/styles';

// FontAwesome imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faPlay, faArrowLeft, faXmark, faGrid2, faDiagramProject, faChevronDown } from '@fortawesome/pro-solid-svg-icons';

// Local imports
import { rateParsingWorkflow, inquiryToAcceptedQuoteWorkflow, bookingToVoyageWorkflow, rfqToBestOfferWorkflow, NODE_KINDS, TRIGGERS, TOOLS, RUNS_ON_LABELS } from './elariomData.js';
import { SectionTitle, Stat, StatRow, Pill } from './ElariomParts.js';
import { BUILD_NOTES } from './elariomBuildNotes.js';
import { AgentRunDialog } from '../admin/RateAgentRun.js';
import ElariomTryIt from './ElariomTryIt.js';
import ElariomRfqDemo from './ElariomRfqDemo.js';
import ElariomCoordinationDemo from './ElariomCoordinationDemo.js';
import { GLOSSARY, SECTION_TITLE } from '../dashboard/rates/chargeGlossary.js';

const NODE_H = 92;

function formatDuration(ms) {
    if (!ms && ms !== 0) return '—';
    const s = Math.round(ms / 1000);
    return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`;
}

// ---------- Canvas ----------

function edgePath(a, b, w, route) {
    // 'back': from a step on a lower row, right along that row, then up into the step's left side.
    if (route === 'back') {
        const x1 = a.x + w;
        const y1 = a.y + NODE_H / 2;
        const xt = b.x - 24;
        const y2 = b.y + NODE_H / 2;
        return { d: `M ${x1} ${y1} L ${xt} ${y1} L ${xt} ${y2} L ${b.x} ${y2}`, lx: x1 + 8, ly: y1 - 8, anchor: 'start' };
    }
    // 'over': up from the top of a step, across above the row, and down into a later step.
    if (route === 'over') {
        const x1 = a.x + w / 2;
        const x2 = b.x + w / 2;
        const top = Math.min(a.y, b.y) - 22;
        return { d: `M ${x1} ${a.y} L ${x1} ${top} L ${x2} ${top} L ${x2} ${b.y}`, lx: (x1 + x2) / 2, ly: top - 6, anchor: 'middle' };
    }
    // 'under': back to an earlier step on the same row: down from a step, left under the row,
    // and up into the earlier one (off-centre, clear of any arrow leaving its middle).
    if (route === 'under') {
        const x1 = a.x + w * 0.3;
        const x2 = b.x + w * 0.7;
        const low = a.y + NODE_H + 34;
        return { d: `M ${x1} ${a.y + NODE_H} L ${x1} ${low} L ${x2} ${low} L ${x2} ${b.y + NODE_H}`, lx: (x1 + x2) / 2, ly: low + 14, anchor: 'middle' };
    }
    // 'left': from a step on a lower row, left along that row, then up into an earlier step.
    if (route === 'left') {
        const y1 = a.y + NODE_H / 2;
        const x2 = b.x + w / 2;
        return { d: `M ${a.x} ${y1} L ${x2} ${y1} L ${x2} ${b.y + NODE_H}`, lx: (a.x + x2) / 2, ly: y1 - 8, anchor: 'middle' };
    }
    // 'downLeft': down from a step, then left into the right side of a step on a lower row.
    if (route === 'downLeft') {
        const x1 = a.x + w / 2;
        const y2 = b.y + NODE_H / 2;
        return { d: `M ${x1} ${a.y + NODE_H} L ${x1} ${y2} L ${b.x + w} ${y2}`, lx: x1 + 8, ly: (a.y + NODE_H + y2) / 2, anchor: 'start' };
    }
    // 'up': straight up, from the top of a step to the bottom of the one above it (knowledge
    // feeding a step on the main row).
    if (route === 'up') {
        const x = a.x + w / 2;
        return { d: `M ${x} ${a.y} L ${x} ${b.y + NODE_H}`, lx: x + 8, ly: (a.y + b.y + NODE_H) / 2, anchor: 'start' };
    }
    if (Math.abs(a.x - b.x) < 1) {
        // Straight down, from the bottom of one step to the top of the one below it.
        return { d: `M ${a.x + w / 2} ${a.y + NODE_H} L ${b.x + w / 2} ${b.y}`, lx: a.x + w / 2 + 8, ly: (a.y + NODE_H + b.y) / 2, anchor: 'start' };
    }
    const x1 = a.x + w;
    const y1 = a.y + NODE_H / 2;
    const x2 = b.x;
    const y2 = b.y + NODE_H / 2;
    return { d: `M ${x1} ${y1} L ${x2} ${y2}`, lx: (x1 + x2) / 2, ly: y1 - 8, anchor: 'middle' };
}

function WorkflowCanvas({ workflow, selected, onSelect, nodeControls = {} }) {
    const theme = useTheme();
    const byId = _.keyBy(workflow.nodes, 'id');
    const w = workflow.nodeWidth;
    const line = alpha(theme.palette.text.secondary, 0.55);

    return (
        <Box sx={{
            overflowX: 'auto', borderRadius: '4px', border: `1px solid ${theme.palette.border1}`,
            backgroundColor: theme.palette.panel1,
            backgroundImage: `radial-gradient(${alpha(theme.palette.text.secondary, 0.22)} 0.8px, transparent 0.8px)`,
            backgroundSize: '16px 16px',
        }}>
            <Box sx={{ position: 'relative', width: `${workflow.width}px`, height: `${workflow.height}px` }}>
                <svg width={workflow.width} height={workflow.height} style={{ position: 'absolute', inset: 0 }} role='img' aria-label={`${workflow.name}: ${workflow.nodes.map(n => n.title).join(', then ')}`}>
                    <defs>
                        <marker id='elariom-arrow' viewBox='0 0 10 10' refX='9' refY='5' markerWidth='7' markerHeight='7' orient='auto-start-reverse'>
                            <path d='M 0 0 L 10 5 L 0 10 z' fill={line} />
                        </marker>
                    </defs>
                    {workflow.edges.map(e => {
                        const a = byId[e.from];
                        const b = byId[e.to];
                        const p = edgePath(a, b, w, e.route);
                        const dashed = a.planned || b.planned || b.inactive || b.kind === 'external' || b.kind === 'knowledge';
                        return (
                            <g key={`${e.from}-${e.to}`}>
                                <path d={p.d} stroke={line} strokeWidth='1.5' fill='none' strokeLinejoin='round' strokeDasharray={dashed ? '5 4' : undefined} markerEnd='url(#elariom-arrow)' />
                                {e.label && (
                                    <text x={p.lx} y={p.ly} textAnchor={p.anchor} fontSize='11' fill={theme.palette.text.secondary}>{e.label}</text>
                                )}
                            </g>
                        );
                    })}
                </svg>
                {workflow.nodes.map(n => {
                    const kind = NODE_KINDS[n.kind];
                    const isSelected = selected === n.id;
                    return (
                        <Box
                            key={n.id}
                            role='button'
                            tabIndex={0}
                            aria-pressed={isSelected}
                            onClick={() => onSelect(isSelected ? null : n.id)}
                            onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && onSelect(isSelected ? null : n.id)}
                            sx={{
                                position: 'absolute', left: n.x, top: n.y, width: w, height: NODE_H, boxSizing: 'border-box',
                                background: '#FFFFFF', borderRadius: '3px', cursor: 'pointer',
                                border: `1px ${n.planned || n.inactive ? 'dashed' : 'solid'} ${isSelected ? kind.color : alpha(kind.color, n.kind === 'agent' ? 0.7 : 0.3)}`,
                                // Agent steps (where a model runs) get a double edge, so the costly steps stand out without colour.
                                boxShadow: isSelected ? `0 0 0 2px ${alpha(kind.color, 0.15)}` : n.kind === 'agent' ? `inset 0 0 0 1px ${alpha(kind.color, 0.12)}` : 'none',
                                padding: '9px 11px', display: 'flex', flexDirection: 'column', gap: '3px',
                                opacity: n.planned ? 0.75 : 1,
                                transition: 'box-shadow 120ms, border-color 120ms',
                                '&:hover': { borderColor: kind.color },
                                '&:focus-visible': { outline: `2px solid ${kind.color}`, outlineOffset: '2px' },
                            }}
                        >
                            {nodeControls[n.id] && (
                                <Box onClick={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()} sx={{ position: 'absolute', top: '2px', right: '2px', opacity: 1 }}>
                                    {nodeControls[n.id]}
                                </Box>
                            )}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', color: alpha(kind.color, 0.7) }}>
                                <FontAwesomeIcon icon={n.icon || kind.icon} style={{ fontSize: '11px' }} />
                                <Typography sx={{ fontSize: '10px', fontWeight: 500, letterSpacing: '0.08em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{kind.label}</Typography>
                            </Box>
                            <Typography sx={{ fontSize: '13px', fontWeight: 600, lineHeight: 1.25, color: n.inactive ? theme.palette.text.secondary : theme.palette.text.primary }}>{n.title}</Typography>
                            <Typography sx={{ fontSize: '11px', color: theme.palette.text.secondary, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{n.subtitle}</Typography>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}

function NodeDetail({ node, onClose }) {
    const theme = useTheme();
    const kind = NODE_KINDS[node.kind];
    return (
        <Paper variant='outlined' sx={{ padding: '14px 16px', borderColor: theme.palette.border1, background: theme.palette.panel1, borderLeft: `2px solid ${kind.color}`, display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: '15px', fontWeight: 600 }}>{node.title}</Typography>
                    <Pill color={kind.color}>{kind.label}</Pill>
                    {node.planned && <Pill color={theme.palette.text.secondary} dashed>Coming soon</Pill>}
                </Box>
                <Button size='small' onClick={onClose} sx={{ minWidth: 0, color: theme.palette.text.secondary }} aria-label='Close step details'>
                    <FontAwesomeIcon icon={faXmark} />
                </Button>
            </Box>
            <Typography sx={{ fontSize: '13px', maxWidth: '760px' }}>{node.what}</Typography>
            {!!node.config?.length && (
                <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {node.config.map(c => <Pill key={c} color={theme.palette.text.secondary}>{c}</Pill>)}
                </Box>
            )}

        </Paper>
    );
}

// Build notes, for the Monada team only (super-admin): collapsed at the bottom of a workflow page.
// Per step what is done and what is still to build (elariomBuildNotes.js), or, for a workflow
// without those, where each step lives in the code.
function BuildNotes({ workflow }) {
    const theme = useTheme();
    const notes = BUILD_NOTES[workflow.id];
    const steps = workflow.nodes
        .map(n => ({ node: n, ...(notes?.steps?.[n.id] || {}) }))
        .filter(s => s.done?.length || s.pending?.length || (!notes && s.node.where));
    if (!steps.length) return null;
    const list = items => <Box component='ul' sx={{ margin: 0, paddingLeft: '18px', fontSize: '12px', lineHeight: 1.6 }}>{items.map(i => <li key={i}>{i}</li>)}</Box>;
    return (
        <Accordion disableGutters elevation={0} sx={{ background: 'transparent', border: `1px dashed ${theme.palette.border1}`, borderRadius: '4px', '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<FontAwesomeIcon icon={faChevronDown} style={{ fontSize: '11px' }} />}>
                <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>Build notes</Typography>
                <Typography sx={{ fontSize: '12px', color: theme.palette.text.secondary, marginLeft: '10px' }}>Monada only · not shown to customers</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {steps.map(({ node, done, pending }) => (
                    <Box key={node.id} sx={{ borderTop: `1px dotted ${theme.palette.border1}`, paddingTop: '10px' }}>
                        <Typography sx={{ fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>{node.title}</Typography>
                        {notes ? (
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: '12px' }}>
                                <Box>
                                    <Typography sx={{ fontSize: '11px', color: theme.palette.text.secondary, marginBottom: '2px' }}>Done</Typography>
                                    {done?.length ? list(done) : <Typography sx={{ fontSize: '12px', color: theme.palette.text.secondary }}>Nothing yet.</Typography>}
                                </Box>
                                <Box>
                                    <Typography sx={{ fontSize: '11px', color: theme.palette.text.secondary, marginBottom: '2px' }}>Still to build</Typography>
                                    {pending?.length ? list(pending) : <Typography sx={{ fontSize: '12px', color: theme.palette.text.secondary }}>Nothing: this part exists.</Typography>}
                                </Box>
                            </Box>
                        ) : (
                            <Typography sx={{ fontSize: '12px', color: theme.palette.text.secondary }}>{node.where}</Typography>
                        )}
                    </Box>
                ))}
                {!!notes?.next?.length && (
                    <Box sx={{ borderTop: `1px dotted ${theme.palette.border1}`, paddingTop: '10px' }}>
                        <Typography sx={{ fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>Next steps</Typography>
                        <Box component='ol' sx={{ margin: 0, paddingLeft: '20px', fontSize: '12px', lineHeight: 1.6 }}>{notes.next.map(t => <li key={t}>{t}</li>)}</Box>
                    </Box>
                )}
            </AccordionDetails>
        </Accordion>
    );
}

// ---------- Runs and knowledge ----------

function RunsTable({ runs, imports, workspaceTitle, accesstoken }) {
    const theme = useTheme();
    const [ open, setOpen ] = useState(null);
    const importsById = _.keyBy(imports, 'id');
    const head = { fontSize: '11px', fontWeight: 600, color: theme.palette.text.secondary, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' };
    const cell = { fontSize: '12px', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' };

    if (!runs.length) {
        return (
            <Typography sx={{ fontSize: '13px', color: theme.palette.text.secondary, padding: '16px 0' }}>
                No agent runs on this workspace yet. Runs are started from Admin → Rate imports while the review step is being built.
            </Typography>
        );
    }

    return (
        <Box sx={{ overflowX: 'auto', border: `1px solid ${theme.palette.border1}`, borderRadius: '8px', background: theme.palette.panel1 }}>
            <Table size='small'>
                <TableHead>
                    <TableRow>
                        {['When', 'Status', 'File', 'Supplier', 'Kind of file', 'Rates', 'Held back', 'Gaps', 'Importer check', 'Time', 'Tokens in / out', 'Model'].map(h => <TableCell key={h} sx={head}>{h}</TableCell>)}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {runs.map(r => {
                        const failedRows = r.validation?.failed || 0;
                        return (
                            <TableRow
                                key={r.id}
                                hover
                                onClick={() => setOpen(r)}
                                sx={{ cursor: 'pointer' }}
                            >
                                <TableCell sx={cell}>{dayjs(r.at).format('MMM D, HH:mm')}</TableCell>
                                <TableCell sx={cell}>
                                    {r.error
                                        ? <Pill color={theme.palette.error.main}>Failed</Pill>
                                        : <Pill color={theme.palette.success.main}>Parsed · awaiting review</Pill>}
                                </TableCell>
                                <TableCell sx={{ ...cell, maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.fileName || ''}>{r.fileName || '—'}</TableCell>
                                <TableCell sx={cell}>{r.supplier?.name || r.supplierOnImport || '—'}</TableCell>
                                <TableCell sx={cell}>{r.classification?.category || '—'}</TableCell>
                                <TableCell sx={cell}>{r.error ? '—' : r.rateCount ?? '—'}</TableCell>
                                <TableCell sx={cell}>{r.error ? '—' : r.heldBackCount ?? 0}</TableCell>
                                <TableCell sx={cell}>{r.error ? '—' : r.gapCount ?? 0}</TableCell>
                                <TableCell sx={cell}>
                                    {r.error ? <Typography component='span' sx={{ fontSize: '12px', color: theme.palette.error.main }} title={r.error}>{String(r.error).slice(0, 40)}</Typography>
                                        : failedRows ? <Typography component='span' sx={{ fontSize: '12px', color: theme.palette.warning.main }}>{failedRows} row{failedRows === 1 ? '' : 's'} rejected</Typography>
                                            : 'All rows accepted'}
                                </TableCell>
                                <TableCell sx={cell}>{formatDuration(r.durationMs)}</TableCell>
                                <TableCell sx={cell}>{r.usage ? `${Math.round(r.usage.input / 1000)}K / ${Math.round(r.usage.output / 1000)}K` : '—'}</TableCell>
                                <TableCell sx={cell}>{r.model || '—'}</TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
            {open && (
                <AgentRunDialog
                    row={{ ...importsById[open.importId], organizationTitle: workspaceTitle }}
                    runSummary={open}
                    accesstoken={accesstoken}
                    onClose={() => setOpen(null)}
                />
            )}
        </Box>
    );
}

function KnowledgeTable({ knowledge }) {
    const theme = useTheme();
    if (!knowledge) {
        return <Typography sx={{ fontSize: '13px', color: theme.palette.text.secondary, padding: '16px 0' }}>The rule book index (routing.json) was not found on the server.</Typography>;
    }
    const head = { fontSize: '11px', fontWeight: 600, color: theme.palette.text.secondary, textTransform: 'uppercase', letterSpacing: '0.04em' };
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Typography sx={{ fontSize: '12px', color: theme.palette.text.secondary }}>
                The Classify step reads {knowledge.classificationRuleCount} classification rules. The Parse step reads one rule pack, chosen by the kind of file. Rule book built {knowledge.generated}.
            </Typography>
            <Box sx={{ overflowX: 'auto', border: `1px solid ${theme.palette.border1}`, borderRadius: '8px', background: theme.palette.panel1 }}>
                <Table size='small'>
                    <TableHead>
                        <TableRow>
                            {['Kind of file', 'Rule pack', 'Rules', 'What it is'].map(h => <TableCell key={h} sx={head}>{h}</TableCell>)}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {knowledge.categories.map(c => (
                            <TableRow key={c.id}>
                                <TableCell sx={{ fontSize: '12px', fontWeight: 500, whiteSpace: 'nowrap' }}>{c.label}</TableCell>
                                <TableCell sx={{ fontSize: '12px', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', whiteSpace: 'nowrap' }}>{c.pack}</TableCell>
                                <TableCell sx={{ fontSize: '12px', fontVariantNumeric: 'tabular-nums' }}>{c.ruleCount}</TableCell>
                                <TableCell sx={{ fontSize: '12px', color: theme.palette.text.secondary, minWidth: '260px' }}>{c.summary}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Box>
            <GlossaryTable />
        </Box>
    );
}

// The charge glossary (chargeGlossary.js): which form section each charge goes in when a rate form
// is drafted. Shown from the code itself, so it always matches what the workflow does.
function GlossaryTable() {
    const theme = useTheme();
    const head = { fontSize: '11px', fontWeight: 600, color: theme.palette.text.secondary, textTransform: 'uppercase', letterSpacing: '0.04em' };
    const entries = Object.entries(GLOSSARY).map(([name, [section, share, count]]) => ({ name, section, share, count }));
    const pretty = n => n.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '16px' }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>Charge glossary</Typography>
            <Typography sx={{ fontSize: '12px', color: theme.palette.text.secondary }}>
                When a rate form is drafted, before review, its charges are put in Origin, Freight and Destination sections by this glossary, whether a script or the agent read the file. The reviewer can move a charge; publishing saves the form as reviewed. {entries.length} charge names come from CargoFive's own sections ({entries.reduce((n, e) => n + e.count, 0).toLocaleString()} charges). A name CargoFive never used is placed by rule: the carrier's own transport and security is Freight, a side named in the charge decides, then the kind of charge, and anything left is Freight.
            </Typography>
            <Box sx={{ overflowX: 'auto', border: `1px solid ${theme.palette.border1}`, borderRadius: '8px', background: theme.palette.panel1 }}>
                <Table size='small'>
                    <TableHead>
                        <TableRow>
                            {['Section', 'Charge names', 'Most common'].map(h => <TableCell key={h} sx={head}>{h}</TableCell>)}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {['o', 'f', 'd'].map(k => {
                            const list = entries.filter(e => e.section === k);
                            return (
                                <TableRow key={k}>
                                    <TableCell sx={{ fontSize: '12px', fontWeight: 500, whiteSpace: 'nowrap' }}>{SECTION_TITLE[k]}</TableCell>
                                    <TableCell sx={{ fontSize: '12px', fontVariantNumeric: 'tabular-nums' }}>{list.length}</TableCell>
                                    <TableCell sx={{ fontSize: '12px', color: theme.palette.text.secondary, minWidth: '260px' }}>{list.slice(0, 8).map(e => pretty(e.name)).join(', ')}</TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </Box>
            <Typography sx={{ fontSize: '12px', color: theme.palette.text.secondary }}>
                The full list and the rules: docs/air-agent/charge-glossary.md. The agents' rule is R11.
            </Typography>
        </Box>
    );
}

// ---------- CargoFive routing switch ----------

// The switch on the CargoFive step: on sends the workspace's FCL and LCL files to CargoFive,
// off sends both to the Monada agent. It writes the same per-workspace setting as Admin →
// Rate imports → Ocean routing, so customers' real uploads follow it from the next file on.
function CargoFiveSwitch({ org, accesstoken, oceanRouting, onChange }) {
    const theme = useTheme();
    const [ confirm, setConfirm ] = useState(null);
    const [ busy, setBusy ] = useState(false);
    const [ error, setError ] = useState(null);
    if (!oceanRouting) return null;

    const on = oceanRouting.fcl === 'cargofive' || oceanRouting.lcl === 'cargofive';
    const workspace = org.title || org.alias;

    const apply = async parser => {
        setBusy(true);
        setError(null);
        try {
            const { data } = await axios.put(`/api/admin/rate-agent/ocean-routing/${org.id}?accesstoken=${accesstoken}`, { shipmentType: 'ALL', parser });
            onChange({ ...oceanRouting, ...data });
            setConfirm(null);
        } catch (e) {
            setError(e.response?.data?.detail || e.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <Tooltip title={on ? 'CargoFive is on for ocean files. Click to turn it off.' : 'CargoFive is off. Click to turn it on for ocean files.'}>
                <Switch
                    size='small'
                    checked={on}
                    onChange={() => setConfirm(on ? 'monada' : 'cargofive')}
                    inputProps={{ 'aria-label': 'Send ocean files to CargoFive' }}
                />
            </Tooltip>
            <Dialog open={!!confirm} onClose={() => !busy && setConfirm(null)} maxWidth='xs' fullWidth>
                <DialogTitle sx={{ fontSize: '17px' }}>{confirm === 'monada' ? 'Turn CargoFive off?' : 'Turn CargoFive on?'}</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: '14px' }}>
                        {confirm === 'monada'
                            ? `From the next upload, FCL and LCL rate files in ${workspace} go to the Monada agent instead of CargoFive.`
                            : `From the next upload, FCL and LCL rate files in ${workspace} go to CargoFive instead of the Monada agent.`}
                    </Typography>
                    <Typography sx={{ fontSize: '13px', color: theme.palette.text.secondary, marginTop: '8px' }}>
                        Files already uploaded stay where they are. This is the same setting as Admin → Rate imports → Ocean routing.
                    </Typography>
                    {error && <Alert severity='error' sx={{ fontSize: '13px', marginTop: '12px' }}>{error}</Alert>}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirm(null)} disabled={busy} sx={{ textTransform: 'none' }}>Cancel</Button>
                    <Button variant='contained' color='monada-button' onClick={() => apply(confirm)} disabled={busy} sx={{ textTransform: 'none' }}>
                        {busy ? 'Saving…' : confirm === 'monada' ? 'Turn off' : 'Turn on'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

// ---------- Elariom in Teams, from a workflow ----------

// The Teams account a workspace's Elariom is usually given (see Connectors → Elariom in Teams).
const suggestedTeamsAccount = org => `elariom.${String(org?.title || org?.alias || 'workspace').toLowerCase().replace(/[^a-z0-9]+/g, '')}@monada.cloud`;

// Connects or disconnects this workspace's Elariom in Teams from a workflow that talks to people
// there, the same as Connectors → Elariom in Teams. Each workspace has its own Teams account: type
// it, and Connect opens Microsoft's sign-in on it in a new tab (a sign-in as another account is
// refused). The status is read again when this tab comes back into focus. Connecting a Teams
// account that answers for another workspace moves it here (src/server/elariom/teams.js).
function TeamsConnection({ teams, org, accesstoken, onChange }) {
    const theme = useTheme();
    const [ confirm, setConfirm ] = useState(false);
    const [ busy, setBusy ] = useState(false);
    const [ error, setError ] = useState(null);
    const [ account, setAccount ] = useState('');
    useEffect(() => { setAccount(teams?.username || suggestedTeamsAccount(org)); }, [teams?.username, org]);
    const accountOk = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(account.trim());
    const sameAsConnected = teams?.connected && account.trim().toLowerCase() === String(teams.username || '').toLowerCase();

    useEffect(() => {
        const refresh = () => axios.get(`/api/elariom/teams/status?accesstoken=${accesstoken}&organizationId=${org.id}`)
            .then(({ data }) => onChange?.(data))
            .catch(() => {});
        window.addEventListener('focus', refresh);
        return () => window.removeEventListener('focus', refresh);
    }, [accesstoken, org.id, onChange]);

    const disconnect = async () => {
        setBusy(true);
        setError(null);
        try {
            const { data } = await axios.delete(`/api/elariom/teams?accesstoken=${accesstoken}&organizationId=${org.id}`);
            onChange?.(data);
            setConfirm(false);
        } catch (e) {
            setError(e.response?.data?.detail || e.message);
        } finally {
            setBusy(false);
        }
    };

    const workspace = org.title || org.alias;
    const text = !teams ? 'Checking…'
        : !teams.configured ? 'Elariom\'s Teams app details are not set on this server.'
            : !teams.connected ? `Not connected. People can't reach ${workspace}'s Elariom in Teams.`
                : `Connected as ${teams.username}, answered by ${RUNS_ON_LABELS[teams.runsOn] || teams.runsOn}.`;

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', padding: '10px 14px', border: `1px solid ${theme.palette.border1}`, borderRadius: '3px' }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>Elariom in Teams</Typography>
            <Typography sx={{ fontSize: '12px', color: theme.palette.text.secondary, flexGrow: 1 }}>{text}</Typography>
            {teams?.configured && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', flexWrap: 'wrap' }}>
                    <TextField
                        size='small'
                        label='Teams account'
                        value={account}
                        onChange={e => setAccount(e.target.value)}
                        error={!!account && !accountOk}
                        sx={{ width: '320px' }}
                        inputProps={{ style: { fontSize: '13px' } }}
                    />
                    {!sameAsConnected && (
                        <Tooltip title='Opens the Microsoft sign-in for this account in a new tab. If the account answers for another workspace, it moves here.'>
                            <span>
                                <Button
                                    size='small'
                                    variant='contained'
                                    disableElevation
                                    disabled={!accountOk}
                                    href={`/api/elariom/teams/login?accesstoken=${accesstoken}&organizationId=${org.id}&account=${encodeURIComponent(account.trim())}`}
                                    target='_blank'
                                    rel='noopener'
                                    sx={{ textTransform: 'none' }}
                                >
                                    {teams.connected ? 'Switch account' : 'Connect'}
                                </Button>
                            </span>
                        </Tooltip>
                    )}
                    {teams.connected && <Button size='small' variant='outlined' onClick={() => setConfirm(true)} sx={{ textTransform: 'none' }}>Disconnect</Button>}
                </Box>
            )}
            {error && !confirm && <Typography sx={{ fontSize: '12px', color: theme.palette.error.main, width: '100%' }}>{error}</Typography>}
            <Dialog open={confirm} onClose={() => !busy && setConfirm(false)} maxWidth='xs' fullWidth>
                <DialogTitle sx={{ fontSize: '17px' }}>Disconnect Elariom from Teams?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: '14px' }}>
                        {`${workspace}'s Elariom stops answering in Teams${teams?.username ? ` as ${teams.username}` : ''}.`}
                    </Typography>
                    <Typography sx={{ fontSize: '13px', color: theme.palette.text.secondary, marginTop: '8px' }}>
                        To use it again, connect it here or on another workspace and sign in again. It then starts from new messages only.
                    </Typography>
                    {error && <Alert severity='error' sx={{ fontSize: '13px', marginTop: '12px' }}>{error}</Alert>}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirm(false)} disabled={busy} sx={{ textTransform: 'none' }}>Cancel</Button>
                    <Button variant='contained' color='monada-button' onClick={disconnect} disabled={busy} sx={{ textTransform: 'none' }}>
                        {busy ? 'Disconnecting…' : 'Disconnect'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

// ---------- Workflow page ----------

function WorkflowPage({ workflow, data, runs, org, accesstoken, onBack, onRoutingChange, isSuperAdmin }) {
    const theme = useTheme();
    const [ selected, setSelected ] = useState(null);
    const [ tab, setTab ] = useState('runs');
    const imports = data?.rateImports || [];
    const node = workflow.nodes.find(n => n.id === selected);
    const ok = runs.filter(r => !r.error);
    const bySource = _.countBy(imports, 'source');

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            <Box
                role='button'
                tabIndex={0}
                onClick={onBack}
                onKeyDown={e => e.key === 'Enter' && onBack()}
                sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: theme.palette.text.secondary, cursor: 'pointer', width: 'fit-content', '&:hover': { color: theme.palette.text.primary } }}
            >
                <FontAwesomeIcon icon={faArrowLeft} style={{ fontSize: '11px' }} /> Workflows
            </Box>
            <SectionTitle
                title={workflow.name}
                blurb={workflow.description}
                right={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Pill color={theme.palette.warning.main}>Live · review & publish on dropped files</Pill>
                        <Tooltip title='Testing a workflow from here comes later. Runs are started from Admin → Rate imports.'>
                            <span><Button size='small' variant='outlined' disabled startIcon={<FontAwesomeIcon icon={faPlay} style={{ fontSize: '10px' }} />} sx={{ textTransform: 'none' }}>Test</Button></span>
                        </Tooltip>
                    </Box>
                }
            />
            <StatRow>
                <Stat value={imports.length} label='Files uploaded' hint={`${bySource.cargofive || 0} via CargoFive · ${(bySource.monada || 0) + (bySource['admin-upload'] || 0)} via Monada`} />
                <Stat value={runs.length} label='Agent runs' hint={`${runs.length - ok.length} failed`} />
                <Stat value={_.sumBy(ok, r => r.rateCount || 0).toLocaleString()} label='Rates parsed' hint='Before review' />
                <Stat value={ok.length ? formatDuration(_.meanBy(ok, r => r.durationMs || 0)) : '—'} label='Average run time' />
            </StatRow>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Box>
                    <Typography sx={{ fontSize: '15px', fontWeight: 600 }}>Try it</Typography>
                    <Typography sx={{ fontSize: '12px', color: theme.palette.text.secondary }}>
                        Drop a supplier's rate file, or their email saved from Outlook as .eml, and see how the agent parses it.
                    </Typography>
                </Box>
                <ElariomTryIt org={org} accesstoken={accesstoken} />
            </Box>

            <Typography sx={{ fontSize: '12px', color: theme.palette.text.secondary }}>
                Click a step to see what it does. Dashed steps are coming soon, or not used on this workspace.
            </Typography>
            <WorkflowCanvas
                workflow={workflow}
                selected={selected}
                onSelect={setSelected}
                nodeControls={{ cargofive: <CargoFiveSwitch org={org} accesstoken={accesstoken} oceanRouting={data?.oceanRouting} onChange={onRoutingChange} /> }}
            />
            {node && <NodeDetail node={node} onClose={() => setSelected(null)} />}

            <Box>
                <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ minHeight: '36px', borderBottom: `1px solid ${theme.palette.border1}`, marginBottom: '12px', '& .MuiTab-root': { textTransform: 'none', fontSize: '13px', minHeight: '36px', padding: '6px 12px' } }}>
                    <Tab value='runs' label={`Runs · ${runs.length}`} />
                    <Tab value='knowledge' label='Knowledge' />
                </Tabs>
                {tab === 'runs' && <RunsTable runs={runs} imports={imports} workspaceTitle={data?.workspace?.title} accesstoken={accesstoken} />}
                {tab === 'knowledge' && <KnowledgeTable knowledge={data?.knowledge} />}
            </Box>
            {isSuperAdmin && <BuildNotes workflow={workflow} />}
        </Box>
    );
}

// A workflow being built, shown as a preview: the chart, what each step does, and which steps
// are available today. Build notes live in docs/air-agent/platform-plan.md, not here.
function PreviewWorkflowPage({ workflow, onBack, isSuperAdmin, org, accesstoken, teams, onTeamsChange }) {
    const theme = useTheme();
    const [ selected, setSelected ] = useState(null);
    const [ demo, setDemo ] = useState(false);
    const hasDemo = ['rfq-to-best-offer', 'booking-to-voyage-completion'].includes(workflow.id);
    const node = workflow.nodes.find(n => n.id === selected);
    const steps = workflow.nodes.filter(n => !['external', 'knowledge'].includes(n.kind));
    const available = steps.filter(n => n.state !== 'planned').length;
    const usesTeams = workflow.nodes.some(n => n.id === 'teams');

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            <Box
                role='button'
                tabIndex={0}
                onClick={onBack}
                onKeyDown={e => e.key === 'Enter' && onBack()}
                sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: theme.palette.text.secondary, cursor: 'pointer', width: 'fit-content', '&:hover': { color: theme.palette.text.primary } }}
            >
                <FontAwesomeIcon icon={faArrowLeft} style={{ fontSize: '11px' }} /> Workflows
            </Box>
            <SectionTitle
                title={workflow.name}
                blurb={workflow.description}
                right={(
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Pill color={theme.palette.text.secondary} dashed>Preview · coming soon</Pill>
                        {hasDemo && <Button variant='contained' disableElevation size='small' startIcon={<FontAwesomeIcon icon={faPlay} style={{ fontSize: '10px' }} />} onClick={() => setDemo(true)} sx={{ textTransform: 'none' }}>Run the demo</Button>}
                    </Box>
                )}
            />
            <StatRow>
                <Stat value={steps.length} label='Steps' hint={`Starts with: ${workflow.trigger}`} />
                <Stat value={available} label='Available today' />
                <Stat value={steps.length - available} label='Coming soon' />
                <Stat value='Teams' label='Where you work with it' hint='Plus the full job in Monada' />
            </StatRow>
            {isSuperAdmin && usesTeams && <TeamsConnection teams={teams} org={org} accesstoken={accesstoken} onChange={onTeamsChange} />}
            <Typography sx={{ fontSize: '12px', color: theme.palette.text.secondary }}>
                Click a step to see what it does. Dashed steps are coming soon.
            </Typography>
            <WorkflowCanvas workflow={workflow} selected={selected} onSelect={setSelected} />
            {node && <NodeDetail node={node} onClose={() => setSelected(null)} />}
            {isSuperAdmin && <BuildNotes workflow={workflow} />}
            {workflow.id === 'rfq-to-best-offer' && <ElariomRfqDemo open={demo} onClose={() => setDemo(false)} org={org} accesstoken={accesstoken} />}
            {workflow.id === 'booking-to-voyage-completion' && <ElariomCoordinationDemo open={demo} onClose={() => setDemo(false)} />}
        </Box>
    );
}

// ---------- Creation wizard (read-only) ----------

const WIZARD_STEPS = ['What to build', 'Trigger', 'Steps', 'Tools & knowledge', 'Test & publish'];

function Option({ title, detail, built, selected }) {
    const theme = useTheme();
    return (
        <Box sx={{
            border: `1px ${built ? 'solid' : 'dashed'} ${selected ? theme.palette.primary.main : theme.palette.border1}`,
            background: selected ? alpha(theme.palette.primary.main, 0.06) : 'transparent',
            borderRadius: '8px', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '2px',
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, fontFamily: title.includes('_') ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : 'inherit' }}>{title}</Typography>
                {built ? <Pill color={theme.palette.success.main}>Available</Pill> : <Pill color={theme.palette.text.secondary} dashed>Planned</Pill>}
            </Box>
            <Typography sx={{ fontSize: '12px', color: theme.palette.text.secondary }}>{detail}</Typography>
        </Box>
    );
}

function CreateWizard({ open, onClose, knowledge }) {
    const theme = useTheme();
    const [ step, setStep ] = useState(0);
    const grid = { display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: '10px' };

    return (
        <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
            <DialogTitle sx={{ fontSize: '18px' }}>New app or workflow</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <Alert severity='info' sx={{ fontSize: '13px' }}>
                    Preview only. This shows the steps of creating a workflow; nothing can be created from here yet. Today workflows are built in code.
                </Alert>
                <Stepper nonLinear activeStep={step} alternativeLabel sx={{ '& .MuiStepLabel-label': { fontSize: '12px' } }}>
                    {WIZARD_STEPS.map((label, i) => (
                        <Step key={label} completed={false}>
                            <StepButton onClick={() => setStep(i)}>{label}</StepButton>
                        </Step>
                    ))}
                </Stepper>
                <Box sx={{ minHeight: '240px' }}>
                    {step === 0 && (
                        <Box sx={grid}>
                            <Paper variant='outlined' sx={{ padding: '16px', borderColor: theme.palette.primary.main, background: alpha(theme.palette.primary.main, 0.04), display: 'flex', gap: '12px' }}>
                                <FontAwesomeIcon icon={faDiagramProject} style={{ color: theme.palette.primary.main, marginTop: '3px' }} />
                                <Box>
                                    <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>Workflow</Typography>
                                    <Typography sx={{ fontSize: '12px', color: theme.palette.text.secondary }}>Work that runs by itself when something happens: a file arrives, an email comes in, a quote changes. Steps can be code, agents, or a person approving.</Typography>
                                </Box>
                            </Paper>
                            <Paper variant='outlined' sx={{ padding: '16px', borderColor: theme.palette.border1, borderStyle: 'dashed', display: 'flex', gap: '12px' }}>
                                <FontAwesomeIcon icon={faGrid2} style={{ color: theme.palette.text.secondary, marginTop: '3px' }} />
                                <Box>
                                    <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>App</Typography>
                                    <Typography sx={{ fontSize: '12px', color: theme.palette.text.secondary }}>A screen for one team, built on the workspace's data and workflows. Planned.</Typography>
                                </Box>
                            </Paper>
                        </Box>
                    )}
                    {step === 1 && (
                        <Box sx={grid}>
                            {TRIGGERS.map(t => <Option key={t.title} {...t} selected={t.title === 'File or email dropped'} />)}
                        </Box>
                    )}
                    {step === 2 && (
                        <Box sx={grid}>
                            {['trigger', 'condition', 'code', 'agent', 'human', 'action'].map(k => {
                                const kind = NODE_KINDS[k];
                                const built = !['human', 'action'].includes(k);
                                const detail = {
                                    trigger: 'What starts the run.',
                                    condition: 'Send the run one way or another, e.g. by mode or workspace setting.',
                                    code: 'A fixed step: read a file, reshape data, check it.',
                                    agent: 'A model with instructions, knowledge and tools, deciding what the data means.',
                                    human: 'Wait for a person to review, edit and approve.',
                                    action: 'Write to the workspace or another system: publish rates, send an email.',
                                }[k];
                                return (
                                    <Box key={k} sx={{ display: 'flex', gap: '10px', alignItems: 'flex-start', border: `1px ${built ? 'solid' : 'dashed'} ${theme.palette.border1}`, borderRadius: '4px', padding: '10px 12px' }}>
                                        <FontAwesomeIcon icon={kind.icon} style={{ color: kind.color, fontSize: '13px', marginTop: '3px' }} />
                                        <Box sx={{ flexGrow: 1 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                                                <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>{kind.label}</Typography>
                                                {built ? <Pill color={theme.palette.success.main}>Used today</Pill> : <Pill color={theme.palette.text.secondary} dashed>Planned</Pill>}
                                            </Box>
                                            <Typography sx={{ fontSize: '12px', color: theme.palette.text.secondary }}>{detail}</Typography>
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                    {step === 3 && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                            <Box>
                                <Typography sx={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: theme.palette.text.secondary, marginBottom: '6px' }}>Tools agents can use</Typography>
                                <Box sx={grid}>{TOOLS.map(t => <Option key={t.name} title={t.name} detail={t.detail} built={t.built} />)}</Box>
                            </Box>
                            <Box>
                                <Typography sx={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: theme.palette.text.secondary, marginBottom: '6px' }}>Knowledge</Typography>
                                <Option
                                    title='Rate file rule book'
                                    detail={knowledge ? `${knowledge.classificationRuleCount} classification rules and ${knowledge.categories.length} rule packs, versioned, learning from gaps and corrections.` : 'Classification rules and one rule pack per kind of rate file.'}
                                    built
                                />
                            </Box>
                        </Box>
                    )}
                    {step === 4 && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            <Option title='Test on a past file' detail='Run the workflow on files this workspace already uploaded, and compare with what was imported. Today: the Run agent button in Admin → Rate imports.' built />
                            <Option title='Replay a period' detail='Run it on every file from a date range and workspace, and score the results.' />
                            <Option title='Publish' detail='Switch the workflow on for this workspace. New runs use the published version; earlier runs keep theirs.' />
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} sx={{ textTransform: 'none' }}>Close</Button>
                <Button disabled={step === 0} onClick={() => setStep(s => s - 1)} sx={{ textTransform: 'none' }}>Back</Button>
                {step < WIZARD_STEPS.length - 1
                    ? <Button variant='contained' color='monada-button' onClick={() => setStep(s => s + 1)} sx={{ textTransform: 'none' }}>Next</Button>
                    : <Button variant='contained' color='monada-button' disabled sx={{ textTransform: 'none' }}>Create</Button>}
            </DialogActions>
        </Dialog>
    );
}

// ---------- List ----------

function WorkflowCard({ workflow, onOpen, badge, footer }) {
    const theme = useTheme();
    const steps = workflow.nodes.filter(n => !['external', 'knowledge'].includes(n.kind));
    return (
        <Paper
            variant='outlined'
            role='button'
            tabIndex={0}
            onClick={onOpen}
            onKeyDown={e => e.key === 'Enter' && onOpen()}
            sx={{
                padding: '16px 18px', cursor: 'pointer', borderColor: theme.palette.border1, background: theme.palette.panel1,
                display: 'flex', flexDirection: 'column', gap: '12px',
                '&:hover': { borderColor: alpha(theme.palette.primary.main, 0.5) },
                '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}` },
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                <Box>
                    <Typography sx={{ fontSize: '15px', fontWeight: 600 }}>{workflow.name}</Typography>
                    <Typography sx={{ fontSize: '13px', color: theme.palette.text.secondary, maxWidth: '820px' }}>{workflow.description}</Typography>
                </Box>
                {badge}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                {steps.map((n, i) => {
                    const kind = NODE_KINDS[n.kind];
                    return (
                        <Box key={n.id} sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <Box title={`${kind.label}: ${n.title}`} sx={{
                                display: 'flex', alignItems: 'center', gap: '5px', padding: '3px 8px', borderRadius: '6px', fontSize: '11px',
                                border: `1px ${n.planned ? 'dashed' : 'solid'} ${alpha(kind.color, 0.5)}`, color: n.planned ? theme.palette.text.secondary : theme.palette.text.primary,
                            }}>
                                <FontAwesomeIcon icon={n.icon || kind.icon} style={{ color: kind.color, fontSize: '10px' }} />
                                {n.title}
                            </Box>
                            {i < steps.length - 1 && <Box sx={{ width: '10px', height: '1px', background: theme.palette.border1 }} />}
                        </Box>
                    );
                })}
            </Box>
            <Typography sx={{ fontSize: '12px', color: theme.palette.text.secondary }}>{footer}</Typography>
        </Paper>
    );
}

function ElariomWorkflows({ data, runs, org, accesstoken, workflowId, openWorkflow, onRoutingChange, onTeamsChange, isSuperAdmin }) {
    const theme = useTheme();
    const [ wizard, setWizard ] = useState(false);
    const workflow = useMemo(() => rateParsingWorkflow({ oceanRouting: data?.oceanRouting, knowledge: data?.knowledge, platform: data?.platform }), [data]);
    const previews = useMemo(() => [inquiryToAcceptedQuoteWorkflow(), rfqToBestOfferWorkflow(), bookingToVoyageWorkflow()], []);

    if (workflowId === workflow.id) {
        return <WorkflowPage workflow={workflow} data={data} runs={runs} org={org} accesstoken={accesstoken} onBack={() => openWorkflow(null)} onRoutingChange={onRoutingChange} isSuperAdmin={isSuperAdmin} />;
    }
    const preview = previews.find(p => p.id === workflowId);
    if (preview) {
        return <PreviewWorkflowPage workflow={preview} onBack={() => openWorkflow(null)} isSuperAdmin={isSuperAdmin} org={org} accesstoken={accesstoken} teams={data?.teams} onTeamsChange={onTeamsChange} />;
    }

    const lastRun = runs[0];

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <SectionTitle
                title='Workflows'
                blurb='Work that runs by itself: something happens, and a chain of code, agents and people turns it into a result in the workspace.'
                right={
                    <Button variant='contained' color='monada-button' size='small' onClick={() => setWizard(true)} startIcon={<FontAwesomeIcon icon={faPlus} style={{ fontSize: '11px' }} />} sx={{ textTransform: 'none' }}>
                        New
                    </Button>
                }
            />
            <WorkflowCard
                workflow={workflow}
                onOpen={() => openWorkflow(workflow.id)}
                badge={<Pill color={theme.palette.warning.main}>Live · review & publish on dropped files</Pill>}
                footer={`Trigger: ${workflow.trigger} · ${runs.length} run${runs.length === 1 ? '' : 's'} on this workspace${lastRun ? ` · last ${dayjs(lastRun.at).format('MMM D, HH:mm')}` : ''}`}
            />
            {previews.map(p => (
                <WorkflowCard
                    key={p.id}
                    workflow={p}
                    onOpen={() => openWorkflow(p.id)}
                    badge={<Pill color={theme.palette.text.secondary} dashed>Preview · coming soon</Pill>}
                    footer={`Starts with: ${p.trigger}`}
                />
            ))}
            <CreateWizard open={wizard} onClose={() => setWizard(false)} knowledge={data?.knowledge} />
        </Box>
    );
}

export default ElariomWorkflows;
